// ────────────────────────────────────────────────────────────────────────
// Seguimiento en vivo — utilidades puras de paradas y clustering
//
// Sin dependencias externas: el clustering es por grilla (grid hashing)
// dependiente del zoom, suficiente para agrupar paradas cercanas y mostrar
// un contador. A mayor zoom la celda se achica y las paradas se separan.
// ────────────────────────────────────────────────────────────────────────

// Paleta semántica funcional de estados de parada (no decorativa).
export const STOP_STATUS = {
  delivered: { key: "delivered", label: "Entregado", color: "#16a34a" },
  route: { key: "route", label: "En ruta", color: "#f59e0b" },
  failed: { key: "failed", label: "Fallido", color: "#ef4444" },
  pending: { key: "pending", label: "Pendiente", color: "#94a3b8" },
  cancelled: { key: "cancelled", label: "Cancelado", color: "#78716c" },
};

// Desempate cuando dos estados empatan en cantidad dentro de un cluster:
// gana el que exige más atención operativa.
const TONE_PRIORITY = ["failed", "route", "pending", "cancelled", "delivered"];

// Código numérico de estado de paquete → clave de estado de parada.
// 0/1/2 (pendiente, recogido, bodega) y 21/22 (devoluciones) caen en "pendiente".
export function statusKey(code) {
  if (code === 4) return "delivered";
  if (code === 3 || code === 30) return "route";
  if (code === 6 || code === 7) return "failed";
  if (code === 5) return "cancelled";
  return "pending";
}

// Coordenada representativa de una parada.
// Entregado → punto real de entrega (coords_*). Resto → destino (shipping_*).
// Devuelve [lat, lng] o null si no hay coordenada usable.
export function pkgCoords(pkg) {
  const delivered = pkg.status === 4;
  const lat = delivered
    ? pkg.coords_latitude ?? pkg.shipping_latitude
    : pkg.shipping_latitude ?? pkg.coords_latitude;
  const lng = delivered
    ? pkg.coords_longitude ?? pkg.shipping_longitude
    : pkg.shipping_longitude ?? pkg.coords_longitude;
  if (lat == null || lng == null) return null;
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  if (la === 0 && ln === 0) return null; // coords nulas codificadas como 0,0
  return [la, ln];
}

// Agrupa paradas por celda de grilla dimensionada al zoom.
// stops: [{ id, lat, lng, key, ... }]
// → [{ lat, lng, count, items, counts:{ [statusKey]: n } }]
export function clusterStops(stops, zoom) {
  // Celda objetivo pequeña: solo agrupa paradas realmente cercanas (≈30px en
  // pantalla). Valores más altos agrupan demasiado pronto y ocultan puntos.
  const TARGET_PX = 30;
  const worldPx = 256 * Math.pow(2, zoom);
  const cell = (360 / worldPx) * TARGET_PX; // grados por celda
  if (!Number.isFinite(cell) || cell <= 0) {
    return stops.map((s) => ({
      lat: s.lat,
      lng: s.lng,
      count: 1,
      items: [s],
      counts: { [s.key]: 1 },
    }));
  }
  const cells = new Map();
  for (const s of stops) {
    const gx = Math.floor(s.lng / cell);
    const gy = Math.floor(s.lat / cell);
    const k = `${gx}:${gy}`;
    let c = cells.get(k);
    if (!c) {
      c = { lat: 0, lng: 0, items: [], counts: {} };
      cells.set(k, c);
    }
    c.items.push(s);
    c.lat += s.lat;
    c.lng += s.lng;
    c.counts[s.key] = (c.counts[s.key] || 0) + 1;
  }
  const out = [];
  for (const c of cells.values()) {
    const n = c.items.length;
    out.push({ lat: c.lat / n, lng: c.lng / n, count: n, items: c.items, counts: c.counts });
  }
  return out;
}

// Tono de un cluster = estado mayoritario por cantidad (mapa de densidad
// honesto). Empates se rompen por prioridad operativa. Los casos puntuales
// (p. ej. un fallido dentro de una zona entregada) se ven al acercar.
export function clusterTone(counts) {
  let best = null;
  let bestN = -1;
  for (const key of TONE_PRIORITY) {
    const n = counts[key] || 0;
    if (n > bestN) {
      bestN = n;
      best = key;
    }
  }
  return STOP_STATUS[best]?.color || STOP_STATUS.pending.color;
}

// Conteos por estado de un repartidor a partir de sus paquetes del día.
// Respeta los conteos que ya entrega el backend cuando existen.
export function driverCounts(driver) {
  const pkgs = driver.packages || [];
  const assigned = driver.totalAssigned ?? pkgs.length;
  const delivered = driver.deliveredCount ?? pkgs.filter((p) => p.status === 4).length;
  const route = driver.inRouteCount ?? pkgs.filter((p) => p.status === 3 || p.status === 30).length;
  const failed = driver.failedCount ?? pkgs.filter((p) => p.status === 6 || p.status === 7).length;
  const cancelled = pkgs.filter((p) => p.status === 5).length;
  const pending = Math.max(0, assigned - delivered - route - failed - cancelled);
  const compliance =
    driver.compliance ??
    (delivered + failed > 0
      ? Math.round((delivered / (delivered + failed)) * 100)
      : 0);
  return { assigned, delivered, route, failed, cancelled, pending, compliance };
}
